import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import isEmpty from "lodash/isEmpty";
import { useLocation, Redirect, useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ReactDOM from "react-dom";

import { useStateSelector } from "utils/field";
import { useOutsideClickHook } from "utils/outsideClick";
import { getBatchModelSummary } from "utils/results/api";
import { getBatchSummaryBaseKey, getBatchSummaryUpgradeKeys } from "utils/results/runIdentity";
import { createKeyboardShortcut } from "utils/keyboardShortcuts";
import { getDashboardSortHandler, getModelSortComparator } from "utils/directory/sorting";
import { getOrgRootFolderId, isOrgRootFolderId, getOrgIdFromFolderId } from "utils/directory/org";
import { addModelsToOrgFolderAPI, removeModelsFromOrgFolderAPI } from "utils/organization/api";
import { readApiResponse } from "utils/api";

// !TODO BUG WITH NAMING
import Button from "components/Button";
import Loading from "components/Loading";
import Dropdown from "components/Dropdown";
import Model from "./Model";
import Folder from "./Folder";
import CreateDialog from "./CreateDialog";
import DeleteDialog from "./DeleteDialog";
import DuplicateDialog from "./DuplicateDialog";
import DeleteMultipleFilesDialog from "./DeleteMultipleFilesDialog";
import OrgRequestDialog from "./OrgRequestDialog";
import NestedPath from "./NestedPath";
import RenameDialog from "./RenameDialog";
import SelectFolder from "../../components/SelectModelFolder";
import GoBack from "./GoBack";
import ComponentListImage from "components/Draggable/DraggableImage/ComponentListImage";

import { ShareFolderDrawer } from "components/Dashboard/Actions";

import ExportSummaryDialog from "./ExportSummaryDialog";
import DashboardTour from "./DashboardTour";
import WhatsNewCarousel from "components/WhatsNewCarousel";
import {
    VERSION_ID as WHATS_NEW_VERSION_ID,
    EYEBROW as WHATS_NEW_EYEBROW,
    SLIDES as WHATS_NEW_SLIDES,
} from "components/WhatsNewCarousel/slides/v1.1.0.jsx";
import LegalUpdateNotice from "components/LegalUpdateNotice";

import dashboardTemp from "assets/images/dashboard_temp.jpg";
import AddIcon from "assets/images/icons/JSX/Add";
import OpenIcon from "assets/images/icons/JSX/Open";
import HouseImage from "assets/images/house-structure.svg";
import SectionHeading from "components/SectionHeading";
import SnackbarAlert from "components/Alert";
import CodeLibIcon from "assets/images/icons/JSX/CodeLib";
import HouseIcon from "assets/images/icons/JSX/House";
import SharedModel from "assets/images/icons/JSX/SharedModel";

import classes from "./style.module.scss";

const csvHeader =
    `Model Name,Upgrade Package,Op. Cond.,Region,HDD,AG Floor Area (m2),BG Floor Area (m2),Volume(m3),ACH,` +
    `Total Consumption (GJ),Electricity (GJ),Natural Gas (GJ),Propane (GJ),Oil (GJ),Wood (GJ),PV (GJ),` +
    `Baseloads (GJ),Total Space Heating (GJ),Primary Space Heating (GJ),Secondary Space Heating (GJ),Total Hot Water (GJ),Primary Hot Water (GJ),Secondary Hot Water (GJ),Space Cooling (GJ),Ventilation (GJ),` +
    `NBC Energy Tier,Overall Energy Performance Improvement vs ERS Ref.,Heat Loss Reduction vs ERS Ref.,Peak Cooling Validation,PCF 1869 - Energy Use Intensity Tier,EUI (GJ/m2),TEDI (kWh/m2),MEUI (kWh/m2),` +
    `Heat Loss Comfort Assessment,Gross Heat Loss(GJ),Auxiliary Heating (MJ),HOT2000 Heat Loss (W),HOT2000 Heat Gain (GJ),F280 Heat Loss (W),F280 Heat Gain (W),` +
    `Total Operational Emissions (t/y),Operational Carbon Intensity (t/y/m2),NBC Proposed Emissions (t/y),NBC Reference Emissions (t/y),NBC Emissions Level,Electricity EF (g/kWh),Natural Gas EF (g/m3),`;

const Dashboard = ({
    name,
    modelDir,
    selectModel,
    uid,
    email,
    fetchUserDir,
    fetchUserOrganization,
    fetchOrgDir,
    error,
    deleteModel,
    removeSharedModelFromDir,
    duplicateModel,
    modelDirLoading,
    createFolder,
    folderLoading,
    deleteFolder,
    renameFolder,
    moveFolder,
    moveModel,
    modelLoading,
    batchFolderRemove,
    batchDeleteModels,
    organization,
    organizationRequested,
    company,
    role,
    agreedToTerms,
    saveUserMeta,
    requestVoltaSnapOrganization,
}) => {
    const { pathname } = useLocation();
    const history = useHistory();

    const [toDelete, toggleToDelete] = useState("");
    const [toRemoveSharedModel, toggleToRemoveSharedModel] = useState("");
    const [newFolderName, setNewFolderName] = useState("");
    const [organizationName, setOrganizationName] = useState("");
    const [folderToDelete, setFolderToDelete] = useState("");
    const [folderName, setFolderName] = useState("");
    const [duplicateName, setDuplicateName] = useState("");
    const [folderToRename, setFolderToRename] = useState("");
    const [folderToMove, setFolderToMove] = useState("");
    const [modelToMove, setModelToMove] = useState("");
    const [modelToDuplicate, setModelToDuplicate] = useState("");
    const [deleting, toggleDeleting] = useState(false);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [toCreate, setToCreate] = useState(false);
    const [reqOrgOpen, setReqOrgOpen] = useState(false);
    const [duplicateLoading, setDuplicateLoading] = useState(false);
    const [selectedFolderModel, setSelectedFolderModel] = useState("");
    const [selectedModelsFolders, setSelectedModelsFolders] = useState([]);
    const [folderModelsToDelete, setFolderModelsToDelete] = useState([]);
    const [dragLock, toggleDragLock] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dropTo, setDropTo] = useState("");
    const [isDropInProcess, setIsDropInProcess] = useState(false);
    const [cursorPosition, setCursorPosition] = useState({ top: 0, left: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [isMultipleDeleting, setIsMultipleDeleting] = useState(false);
    const [orgRequestLoading, setOrgRequestLoading] = useState(false);
    const [folderToShare, setFolderToShare] = useState("");
    const [modelsToSummarize, setModelsToSummarize] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [sortBy, setSortBy] = useState("name");
    const [sortOrder, setSortOrder] = useState("asc");
    const [alert, setAlert] = useState({
        open: false,
        message: "",
        anchorOrigin: { vertical: "top", horizontal: "center" },
        direction: "down",
    });

    const showAlert = (message, severity = "success") =>
        setAlert({
            open: true,
            message,
            anchorOrigin: { vertical: "top", horizontal: "center" },
            direction: "down",
            severity,
        });

    const createRef = useOutsideClickHook(() => setIsCreateOpen(false));

    const scrollContainerRef = useRef(null);
    const folderModelRef = useRef(null);

    const isUserVerified = useStateSelector("user.isUserVerified");

    const path = pathname.split("/").filter((str) => str !== "" && str !== "dashboard") || [];

    const currentFolderId = path[path.length - 1] || null;

    const { primaryOrg = "", ...restMemberOrgIds } = organization?.memberOrgIds || {};

    // Every organization the user belongs to, primary first. Names come from the user document, so
    // the folders render without fetching anything. Entries are objects keyed by org id; anything
    // else in there is not a membership and must not be fetched or seeded.
    const orgIds = [
        ...new Set(
            [
                primaryOrg,
                ...Object.keys(restMemberOrgIds)
                    .filter((id) => typeof restMemberOrgIds[id] === "object" && id !== primaryOrg)
                    .sort((a, b) => (restMemberOrgIds[a]?.name || "").localeCompare(restMemberOrgIds[b]?.name || "")),
            ].filter(Boolean)
        ),
    ];

    const orgTrees = organization?.modelDir || {};
    const getOrgName = (id) => restMemberOrgIds[id]?.name || organization?.details?.[id]?.name || "Organization";
    const getOrgTree = (id) => orgTrees[id] || {};

    // The shared folder is only ever a root-level folder, so the first path segment decides the tree.
    const isInOrgTree = isOrgRootFolderId(path[0]);
    const orgTreeId = isInOrgTree ? getOrgIdFromFolderId(path[0]) : null;

    // Writes go to the org document while browsing the shared tree, to the user's own otherwise.
    const mutationOrgId = isInOrgTree ? orgTreeId : null;

    // The tree and name of the organization whose folder is open, when one is.
    const orgDir = getOrgTree(orgTreeId);
    const orgName = getOrgName(orgTreeId);
    const orgDirError = Boolean(organization?.orgDirError?.[orgTreeId]);

    // The shared folder's own level maps to parentFolderId null inside the org document.
    const currentDirFolderId = isInOrgTree && isOrgRootFolderId(currentFolderId) ? null : currentFolderId;

    const refetchCurrentDir = (skipLoading = true) =>
        isInOrgTree ? fetchOrgDir(orgTreeId, skipLoading) : fetchUserDir(uid, skipLoading);

    const hasFiveModels = isUserVerified
        ? false
        : !isEmpty(modelDir?.models)
          ? Object.keys(modelDir.models).length >= 5
          : false;

    const onMouseMove = useCallback((e) => {
        const { pageY, pageX } = e;

        const top = pageY - window.scrollY + 10;
        const left = pageX - window.scrollX + 10;

        setCursorPosition({ top, left });
    }, []);

    useEffect(() => {
        if (uid) {
            fetchUserDir(uid, false);
        }
    }, [uid]);

    // Every membership's tree, so each shared folder shows its own contents and the move dialog can
    // offer all of them. One small read per organization, and nearly everyone has exactly one.
    useEffect(() => {
        orgIds.forEach((id) => fetchOrgDir(id, true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgIds.join(",")]);

    // Member names and admin ids for the organization being browsed, used for model ownership.
    useEffect(() => {
        if (uid && orgTreeId && !organization?.details?.[orgTreeId]) {
            fetchUserOrganization(uid, orgTreeId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, orgTreeId]);

    useEffect(() => {
        // Update loading state once modelDir has loaded
        if (!isEmpty(modelDir)) {
            setIsLoading(false);
        }
    }, [modelDir]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (folderModelRef.current && !folderModelRef.current.contains(event.target)) {
                setSelectedFolderModel("");
                setSelectedModelsFolders([]);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            onMouseMove(e);
        };

        if (!isLoading) return;

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("wheel", handleMouseMove);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("wheel", handleMouseMove);
        };
    }, [isLoading, onMouseMove]);

    const [firstName = ""] = name.split(" ");
    const { models: singleModels = {}, folders = {} } = isInOrgTree ? orgDir : modelDir;

    // The move dialog navigates one combined tree: the personal folders at the root, the shared
    // folder beside them, and the org's own folders hanging off it. Entries at the root of the org
    // document carry parentFolderId null, so they are re-parented onto the shared folder's id.
    const underSharedFolder = (entries = {}, rootFolderId) =>
        Object.entries(entries).reduce(
            (acc, [key, entry]) => ({
                ...acc,
                [key]: { ...entry, parentFolderId: entry.parentFolderId || rootFolderId },
            }),
            {}
        );

    // One synthetic root per organization, each carrying that organization's own folders.
    const mergeAcrossOrgs = (pick) => orgIds.reduce((acc, id) => ({ ...acc, ...pick(id, getOrgRootFolderId(id)) }), {});

    const sharedRootFolders = orgIds.reduce(
        (acc, id, index) => ({
            ...acc,
            [getOrgRootFolderId(id)]: {
                name: getOrgName(id),
                parentFolderId: null,
                disableDelete: true,
                // the move dialog reads these to match the dashboard: shared folders first, in
                // the same order, with the same icon
                isOrgFolder: true,
                orgOrder: index,
            },
        }),
        {}
    );

    const moveDestinationFolders = {
        ...(modelDir.folders || {}),
        ...sharedRootFolders,
        ...mergeAcrossOrgs((id, rootFolderId) => underSharedFolder(getOrgTree(id).folders, rootFolderId)),
    };

    const moveDestinationModels = {
        ...(modelDir.models || {}),
        ...mergeAcrossOrgs((id, rootFolderId) => underSharedFolder(getOrgTree(id).models, rootFolderId)),
    };

    // Breadcrumbs can span both trees, so they need every folder the path can name.
    const pathFolders = {
        ...(modelDir.folders || {}),
        ...mergeAcrossOrgs((id) => getOrgTree(id).folders || {}),
        ...sharedRootFolders,
    };

    const sendOrganizationRequest = async () => {
        saveUserMeta({ uid, userMeta: { organizationRequested: true } });

        return await requestVoltaSnapOrganization({ uid, userName: name, userEmail: email, orgName: organizationName });
    };

    const startDelete = (event, modelId) => {
        event.stopPropagation();
        toggleToDelete(modelId);
    };

    const startFolderDelete = (event, folderId) => {
        event.stopPropagation();
        setFolderToDelete(folderId);
    };

    const startSharedModelRemove = (event, modelId) => {
        event.stopPropagation();
        toggleToRemoveSharedModel(modelId);
    };

    const getOrgMemberName = (memberUid) =>
        organization?.details?.[orgTreeId]?.members?.[memberUid]?.name || "another member";

    // Takes a model back out of the shared folder and into the owner's own directory.
    const removeModelFromOrgFolder = async (event, modelId) => {
        event.stopPropagation();

        const { ownerUid = "" } = orgDir.models?.[modelId] || {};
        const belongsToOther = Boolean(ownerUid && ownerUid !== uid);

        setIsLoading(true);

        const {
            data: { hasErrors = false } = {},
            status,
            message,
        } = readApiResponse(
            await removeModelsFromOrgFolderAPI({
                uid,
                orgId: orgTreeId,
                modelIds: [modelId],
            })
        );

        if (status !== 200) {
            console.error("Error moving model out of the shared folder:", message);
            showAlert(message || "Could not move the model out of the shared folder. Please try again.", "error");
        } else if (hasErrors) {
            showAlert(
                "Only the owner or an organization admin can move this model out of the shared folder.",
                "warning"
            );
        } else {
            showAlert(
                belongsToOther
                    ? `Model returned to ${getOrgMemberName(ownerUid)}`
                    : "Model moved to your Model Directory"
            );
        }

        await Promise.all([fetchUserDir(uid, true), fetchOrgDir(orgTreeId, true)]);

        setIsLoading(false);
    };

    const sortComparator = getModelSortComparator(sortBy, sortOrder);
    const handleSort = useMemo(
        () => getDashboardSortHandler(sortBy, setSortBy, sortOrder, setSortOrder),
        [sortBy, setSortBy, sortOrder, setSortOrder]
    );

    const filteredFolders = !isEmpty(folders)
        ? Object.entries(folders)
              // A legacy "{org} (Shared)" folder from the old fan-out still renders as an ordinary
              // folder. It is keyed by the bare orgId, so it never collides with the shared folder,
              // and hiding it would hide the models that were shared into it.
              .filter(([key, { parentFolderId }]) => parentFolderId === currentDirFolderId)
              .sort(sortComparator)
              .map(([key, el]) => [key, { ...el, type: "folder" }])
        : [];

    const filteredModels = !isEmpty(singleModels)
        ? Object.entries(singleModels)
              .filter(([key, { parentFolderId }]) =>
                  parentFolderId ? parentFolderId === currentDirFolderId : currentDirFolderId ? false : true
              )
              .sort(sortComparator)
              .map(([key, el]) => [key, { ...el, type: "model" }])
        : [];

    // One shared folder per organization, at the top of the personal root. Each one's contents come
    // from that organization's document, not from the personal directory.
    const lastEditedInTree = (tree = {}) =>
        [...Object.values(tree.folders || {}), ...Object.values(tree.models || {})]
            .map(({ lastEdited }) => lastEdited)
            .filter(Boolean)
            .sort()
            .pop();

    const sharedFolderEntries =
        !isInOrgTree && currentDirFolderId === null
            ? orgIds.map((id) => [
                  getOrgRootFolderId(id),
                  {
                      name: getOrgName(id),
                      type: "folder",
                      lastEdited: lastEditedInTree(getOrgTree(id)),
                      sharedWithUser: true,
                      isOrgFolder: true,
                      // it is not a real directory entry: it cannot be renamed, moved or deleted
                      disableDelete: true,
                  },
              ])
            : [];

    let createNewOptions = [
        {
            label: "New Model",
            // a new model is always created in the user's own directory, then moved into the
            // shared folder, so creating straight into the shared tree is not offered
            disabled: hasFiveModels || isInOrgTree,
            onClick: () => {
                history.push(`/create${currentDirFolderId && !isInOrgTree ? `?folderId=${currentDirFolderId}` : ""}`);
                setIsCreateOpen(false);
            },
        },
    ];

    if (path.length <= 6) {
        createNewOptions = [
            ...createNewOptions,
            {
                label: "New Folder",
                onClick: () => {
                    setToCreate(true);
                    setIsCreateOpen(false);
                },
            },
        ];
    }

    const allFoldersModels = [...sharedFolderEntries, ...filteredFolders, ...filteredModels];
    const firstModelKey = allFoldersModels.find(([, el]) => el.type === "model")?.[0];

    const onClick = async (event, type, key, index) => {
        event.preventDefault();

        // Pressed shift, multi select items from selected to clicked
        if (event.shiftKey && !event.ctrlKey && !event.metaKey) {
            multipleModelFolderSelect(event, index);

            return;
        }

        // Pressed ctrl and clicked element is not in multiselected items so select item
        if (
            (event.ctrlKey || event.metaKey) &&
            !selectedModelsFolders.includes(key) &&
            selectedFolderModel &&
            selectedModelsFolders
        ) {
            setSelectedModelsFolders([...selectedModelsFolders, key]);
            checkDraggability([...selectedModelsFolders, key]);

            return;
        }

        // Pressed ctrl and clicked element is already in multiselected items so deselect item
        if (
            (event.ctrlKey || event.metaKey) &&
            selectedModelsFolders.includes(key) &&
            selectedFolderModel &&
            selectedModelsFolders
        ) {
            const newFoldersAndModels = selectedModelsFolders.filter((id) => id !== key);

            // Update selectedFolderModel if it's being removed from multiSelectedItems so item is deselected
            if (selectedFolderModel === key) {
                setSelectedFolderModel(newFoldersAndModels[0]);
            }

            setSelectedModelsFolders(newFoldersAndModels);
            checkDraggability(newFoldersAndModels);

            return;
        }

        // Select first item
        if (!selectedFolderModel || selectedFolderModel !== key) {
            setSelectedFolderModel(key);

            multipleModelFolderSelect(event, index);
        }

        // Open selected folder when clicked if no other items are selected, otherwise deselect the rest
        if (selectedFolderModel && selectedFolderModel === key && type === "folder") {
            if (selectedModelsFolders.length > 1) {
                setSelectedFolderModel(key);
                setSelectedModelsFolders([key]);
                checkDraggability([key]);
            } else {
                history.push(`${pathname === "/" ? "/dashboard" : pathname}/${key}`);

                setSelectedFolderModel("");
            }
        }

        // Open selected item when clicked if no other items are selected, otherwise deselect the rest
        if (selectedFolderModel && selectedFolderModel === key && type === "model") {
            if (selectedModelsFolders.length > 1) {
                setSelectedFolderModel(key);
                setSelectedModelsFolders([key]);
                checkDraggability([key]);
            } else {
                setIsLoading(true);

                await selectModel(key);
            }

            // setSelectedFolderModel(""); // causes memory leak from updating react state after dismounting
        }
    };

    const multipleModelFolderSelect = (event, index) => {
        event.preventDefault();
        var selectedItems = [];

        if (!event.shiftKey && (selectedModelsFolders.length <= 1 || selectedModelsFolders.length > 1)) {
            // setSelectedModelsFolders([allFoldersModels[index][0]]);
            selectedItems = [allFoldersModels[index][0]];
        }

        if (event.shiftKey && selectedModelsFolders.length === 0) {
            // setSelectedModelsFolders([allFoldersModels[index][0]]);
            selectedItems = [allFoldersModels[index][0]];
        }

        if (event.shiftKey && selectedModelsFolders.length > 0) {
            // setSelectedFolderModel("");

            const firstIndex = allFoldersModels.findIndex(([key, el]) => key == selectedModelsFolders[0]);
            const startIndex =
                selectedModelsFolders.length > 1 && firstIndex >= index
                    ? allFoldersModels.findIndex(
                          ([key, el]) => key == selectedModelsFolders[selectedModelsFolders.length - 1]
                      )
                    : firstIndex;
            const endIndex = selectedModelsFolders.includes(allFoldersModels[index][0]) ? index : index;

            const selectedModels =
                endIndex < startIndex
                    ? allFoldersModels.slice(endIndex, startIndex + 1)
                    : allFoldersModels.slice(startIndex, endIndex + 1);

            // setSelectedModelsFolders(selectedModels.map(([key, _]) => key));
            selectedItems = selectedModels.map(([key, _]) => key);
        }

        setSelectedModelsFolders(selectedItems);
        checkDraggability(selectedItems);
    };

    const checkDraggability = (selectedItems) => {
        for (let itemId of selectedItems) {
            const index = allFoldersModels.findIndex(([key, el]) => key == itemId);
            const [key, el] = allFoldersModels[index];
            if (el.sharedWithUser) {
                toggleDragLock(true);
                return;
            }
        }
        toggleDragLock(false);
    };

    // One router for every move, whichever gesture started it. A destination of null means the
    // personal root, the shared folder's own id means the root of the org tree, anything else is a
    // folder in the tree currently being browsed.
    const moveItemsTo = async ({ modelIds = [], folderIds = [], destinationId }) => {
        const targetIsSharedFolder = isOrgRootFolderId(destinationId);

        // The dialog can pick a folder in either tree, so the destination decides which document
        // it belongs to. Dragging can only ever target the level being browsed, which this covers.
        const destinationOrgId = targetIsSharedFolder
            ? getOrgIdFromFolderId(destinationId)
            : destinationId == null
              ? null
              : orgIds.find((id) => getOrgTree(id).folders?.[destinationId]) ||
                (modelDir.folders?.[destinationId] ? null : mutationOrgId);
        const destinationFolderId = targetIsSharedFolder ? null : destinationId;
        const isCrossingBoundary = mutationOrgId !== destinationOrgId;

        if (isCrossingBoundary && !isEmpty(folderIds)) {
            showAlert("Folders can't be moved in or out of the shared folder. Move the models inside them.", "warning");

            return;
        }

        if (!isCrossingBoundary) {
            await Promise.all([
                ...modelIds.map((id) => moveModel(id, uid, destinationFolderId, true, destinationOrgId)),
                ...folderIds.map((id) => moveFolder(id, uid, destinationFolderId, true, destinationOrgId)),
            ]);

            await refetchCurrentDir(true);

            return;
        }

        const {
            data: { hasErrors = false } = {},
            status,
            message,
        } = readApiResponse(
            destinationOrgId
                ? await addModelsToOrgFolderAPI({
                      uid,
                      orgId: destinationOrgId,
                      modelIds,
                      parentFolderId: destinationFolderId,
                      // models can come straight from another organization's shared folder, and
                      // that tree has to lose the entry in the same write that adds the new one
                      fromOrgId: mutationOrgId,
                  })
                : await removeModelsFromOrgFolderAPI({ uid, orgId: mutationOrgId, modelIds })
        );

        if (status !== 200) {
            // the server writes the org entry before it cleans the personal one, so a failure
            // here can leave the model in both trees until the cause is fixed
            console.error("Error moving models across the shared folder boundary:", message);
            showAlert(
                message ||
                    (destinationOrgId
                        ? "Could not move the models into the shared folder. Please try again."
                        : "Could not move the models out of the shared folder. Please try again."),
                "error"
            );
        } else if (hasErrors) {
            showAlert(
                destinationOrgId
                    ? "Some models were skipped. You can only share models you have write access to."
                    : "Some models were skipped. Only the owner or an organization admin can move them out of the shared folder.",
                "warning"
            );
        }

        // The server hands a model back to its owner's root, so a chosen personal folder needs a
        // second hop. Only for models this user owns: another member's lands in their directory.
        if (status === 200 && !destinationOrgId && destinationFolderId) {
            const ownModelIds = modelIds.filter((id) => (orgDir.models?.[id] || {}).ownerUid === uid);

            await Promise.all(ownModelIds.map((id) => moveModel(id, uid, destinationFolderId, true, null)));
        }

        // a crossing move rewrites both documents, and both organizations when it crosses from one
        // shared folder into another
        await Promise.all([
            fetchUserDir(uid, true),
            ...[...new Set([destinationOrgId, mutationOrgId])].filter(Boolean).map((id) => fetchOrgDir(id, true)),
        ]);
    };

    // The move dialog names a single item. When it is part of a multi-selection, the whole
    // selection moves, which is what picking several and choosing Move looks like it should do.
    const moveSelection = async (itemId, destinationId) => {
        const itemIds =
            selectedModelsFolders.includes(itemId) && selectedModelsFolders.length > 1
                ? selectedModelsFolders
                : [itemId];

        const items = itemIds.map((id) => allFoldersModels.find(([key]) => key === id)).filter(Boolean);

        setIsLoading(true);

        await moveItemsTo({
            modelIds: items.filter(([, { type }]) => type === "model").map(([key]) => key),
            folderIds: items.filter(([, { type }]) => type === "folder").map(([key]) => key),
            destinationId,
        });

        setIsLoading(false);
    };

    const onDragEnd = async (event) => {
        event.preventDefault();

        setCursorPosition({ top: event.clientY, left: event.clientX - 250 });

        setIsDragging(false);

        if (dropTo === "") return;

        const draggedIds =
            selectedFolderModel && selectedModelsFolders.length <= 1
                ? [selectedFolderModel]
                : !selectedModelsFolders.includes(dropTo) && selectedModelsFolders.length > 1
                  ? selectedModelsFolders
                  : [];

        const dragged = draggedIds.map((id) => allFoldersModels.find(([key]) => key === id)).filter(Boolean);

        if (isEmpty(dragged)) {
            setDropTo("");

            return;
        }

        setIsLoading(true);
        setIsDropInProcess(true);

        await moveItemsTo({
            modelIds: dragged.filter(([, { type }]) => type === "model").map(([key]) => key),
            folderIds: dragged.filter(([, { type }]) => type === "folder").map(([key]) => key),
            destinationId: dropTo,
        });

        setIsLoading(false);
        setIsDropInProcess(false);
        setDropTo("");
        setSelectedFolderModel("");
        if (selectedModelsFolders.length > 1) setSelectedModelsFolders([]);
    };

    const onDragStart = (event, draggableId, folderName, type) => {
        setIsDragging(true);

        event.dataTransfer.effectAllowed = "move";
        // event.preventDefault();

        var selectedItems = selectedModelsFolders;

        // Reselect items if dragging starts on an unselected item
        if (!selectedModelsFolders.includes(draggableId)) {
            setSelectedFolderModel(draggableId);
            setSelectedModelsFolders([draggableId]);
            checkDraggability([draggableId]);
            selectedItems = [draggableId];
        }

        const image = (
            <ComponentListImage componentLabel={folderName} componentType={type} multipleSelect={selectedItems} />
        );

        let ghost = document.createElement("div");

        ghost.style.transform = "translate(-10000px, -10000px)";
        ghost.style.position = "absolute";
        document.body.appendChild(ghost);
        event.dataTransfer.setDragImage(ghost, 0, 0);

        ReactDOM.render(image, ghost);
    };
    const onDragUpdate = (event, dropToId, type) => {
        if (!isDragging) return;

        event.preventDefault();

        if (dropToId === dropTo) return;

        if (type === "folder") {
            if (!selectedModelsFolders.includes(dropToId) && selectedModelsFolders > 0) {
                setDropTo(dropToId);
            }

            if (selectedFolderModel !== dropToId && !selectedModelsFolders.includes(dropToId)) {
                setDropTo(dropToId);
            }
        }

        if (type === "model" && dropTo !== "") {
            setDropTo("");
        }
    };

    const handleMultipleDelete = async () => {
        setIsMultipleDeleting(true);

        let foldersBatchDelete = [];
        let modelsBatchToDelete = [];

        folderModelsToDelete.forEach(async (folderModel) => {
            const modelFolder = allFoldersModels.find(([key, {}]) => key === folderModel);

            const modelFolderId = modelFolder[0];
            const modelFolderType = modelFolder[1].type;

            if (modelFolderType === "folder") {
                foldersBatchDelete.push(modelFolderId);
            }

            if (modelFolderType === "model") {
                modelsBatchToDelete.push(modelFolderId);
            }
        });

        // Deleting a shared model would delete it for the whole organization, so the shared tree
        // only allows folder deletion here. Models leave through the shared folder first.
        if (isInOrgTree && modelsBatchToDelete.length > 0) {
            showAlert(
                "Models in the shared folder can't be deleted here. Move them to a personal directory first.",
                "warning"
            );
        }

        await batchFolderRemove(foldersBatchDelete, uid, mutationOrgId);
        if (!isInOrgTree) await batchDeleteModels(modelsBatchToDelete, uid);

        await refetchCurrentDir(false);

        setIsMultipleDeleting(false);
        setSelectedModelsFolders([]);
        setFolderModelsToDelete([]);
    };

    const handleBatchSummaryExport = async () => {
        setExportLoading(true);

        // await exportModelSummary({ modelIds: modelsToSummarize });
        try {
            const { data: { modelResultsSummary } = {} } = await getBatchModelSummary({ modelIds: modelsToSummarize });

            if (!isEmpty(modelResultsSummary)) {
                const formattedResults = modelResultsSummary.reduce((cache, curr) => {
                    const { modelSummary: { modelId = "", cliId = "", modelName = "" } = {}, resultsSummary = {} } =
                        curr;

                    const baseResKey = getBatchSummaryBaseKey(cliId, resultsSummary);

                    const { [baseResKey]: { baseResultsDetails = {} } = {} } = resultsSummary;

                    const resultsObj = getBatchSummaryUpgradeKeys(cliId).reduce((acc, pkgId) => {
                        const { [pkgId]: { upgradeResultsDetails = {} } = {} } = resultsSummary;
                        return {
                            ...acc,
                            [`${modelId}-${pkgId}`]: {
                                modelName,
                                ...upgradeResultsDetails,
                            },
                        };
                    }, {});

                    return {
                        ...cache,
                        [`${modelId}-base`]: {
                            modelName,
                            upgradePackageName: "",
                            ...baseResultsDetails,
                        },
                        ...resultsObj,
                    };
                }, {});

                const csv = Object.keys(formattedResults).reduce((cache, curr, i) => {
                    if (isEmpty(formattedResults[curr])) {
                        return cache;
                    }
                    const {
                        modelName = "",
                        upgradePackageName = "",
                        selectedOpCond = "",
                        provTerrCode = "",
                        hdd = "",
                        agFloorArea = "",
                        bgFloorArea = "",
                        totalVolume = "",
                        airChangeRate50Pa = "",
                        annualEnergyConsumption = "",
                        grossElectricity = "",
                        grossNaturalGas = "",
                        grossPropane = "",
                        grossOil = "",
                        grossWood = "",
                        pvProduction = "",
                        baseload = "",
                        totalSpaceHeating = "",
                        primarySpaceHeating = "",
                        secondarySpaceHeating = "",
                        totalHotWater = "",
                        primaryHotWater = "",
                        secondaryHotWater = "",
                        spaceCooling = "",
                        ventilation = "",
                        nbcEnergyTier = "",
                        overallImprovementVsRef = "",
                        grossHeatLossImprovementVsRef = "",
                        peakCoolingValidation = "",
                        pcf1869EnergyUseIntensity = "",
                        eui = "",
                        tedi = "",
                        meui = "",
                        //Heat Loss
                        heatlossComfort = "",
                        grossHeating = "",
                        auxiliaryEnergy = "",
                        designHeatLossRate = "",
                        designCoolLossRate = "",
                        f280HeatLoss = "",
                        f280HeatGain = "",
                        //Carbon
                        totalOpEmissions_tCO2 = "",
                        opCarbonIntensity_tCO2m2 = "",
                        nbcProposedEmissions = "",
                        nbcRefEmissions = "",
                        nbcEmissionsLevel = "",
                        //Emission Factors
                        elecEf = "",
                        ngEf = "",
                    } = formattedResults[curr];

                    return (
                        `${cache}\r\n` +
                        `${modelName},${upgradePackageName},${selectedOpCond},${provTerrCode},${hdd},${agFloorArea},${bgFloorArea},${totalVolume},${airChangeRate50Pa},` +
                        `${annualEnergyConsumption},${grossElectricity},${grossNaturalGas},${grossPropane},${grossOil},${grossWood},${pvProduction},` +
                        `${baseload},${totalSpaceHeating},${primarySpaceHeating},${secondarySpaceHeating},${totalHotWater},${primaryHotWater},${secondaryHotWater},${spaceCooling},${ventilation},` +
                        `${nbcEnergyTier},${overallImprovementVsRef},${grossHeatLossImprovementVsRef},${peakCoolingValidation},${pcf1869EnergyUseIntensity},${eui},${tedi},${meui},` +
                        `${heatlossComfort},${grossHeating},${auxiliaryEnergy},${designHeatLossRate},${designCoolLossRate},${f280HeatLoss},${f280HeatGain},` +
                        `${totalOpEmissions_tCO2},${opCarbonIntensity_tCO2m2},${nbcProposedEmissions},${nbcRefEmissions},${nbcEmissionsLevel},${elecEf},${ngEf},`
                    );
                }, csvHeader);

                let cb = new Blob([csv], { type: "text/csv" });

                let url = window.URL.createObjectURL(cb);
                let anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `model summary.csv`;

                anchor.click();
                window.URL.revokeObjectURL(url);
                anchor.remove();
            }
        } catch (err) {
            console.log("error", err?.message);
        }

        setModelsToSummarize([]);
        setExportLoading(false);
    };

    // The shared folder's id is synthetic, so it is never a key in either directory document. It is
    // a valid root as long as the path names an organization this user belongs to.
    const isKnownRootFolder = isInOrgTree ? orgIds.includes(orgTreeId) : Boolean(folders[path[0]]);

    if (!isLoading && path.length > 0 && !isKnownRootFolder) {
        return <Redirect to="/404-page-not-found" />;
    }

    // throw new Error("BREAKING EVERYTHING MUAHHAHAHA");

    return (
        <div
            className={classes.dashboard}
            onMouseMove={onMouseMove}
            style={{ pointerEvents: isLoading ? "none" : "auto" }}
            ref={scrollContainerRef}
        >
            <div style={{ position: "fixed", pointerEvents: "none", ...cursorPosition }}>
                {isLoading && <Loading className={classes.smallerSpinner} subClassName={classes.smallSpinner} />}
            </div>

            <div className={classes.dashboardHead} style={{ backgroundImage: `url(${dashboardTemp})` }}>
                <img src={HouseImage} alt="House drawing" />
                <div className={classes.text}>
                    <h1>Hi {firstName},</h1>
                    <p>Welcome to your Volta SNAP dashboard</p>
                    <div className={classes.buttonContainer}>
                        <Button
                            icon={AddIcon}
                            type="white"
                            large
                            disabled={hasFiveModels}
                            className={classes.button}
                            data-dashboard-tour="create-new-model"
                            onClick={() =>
                                history.push(`/create${currentFolderId ? `?folderId=${currentFolderId}` : ""}`)
                            }
                        >
                            Create New Model
                        </Button>
                        {/* <a rel="noopener noreferrer" href="https://forms.gle/KKRDTYPUwHvULpHs5" target="_blank">
                            <Button icon={OpenIcon} type="highlight" large>
                                Advisory Panel Survey
                            </Button>
                        </a> */}
                    </div>
                </div>
            </div>
            <WhatsNewCarousel versionId={WHATS_NEW_VERSION_ID} eyebrow={WHATS_NEW_EYEBROW} slides={WHATS_NEW_SLIDES} />
            {modelDirLoading && <Loading className={classes.loading} message="Loading House Models" />}
            {/* the new user screen is about the personal directory, never about an empty shared folder */}
            {!modelDirLoading &&
                !isInOrgTree &&
                isEmpty(singleModels) &&
                isEmpty(folders) &&
                isEmpty(sharedFolderEntries) && (
                    <div className={classes.dashboardEmpty}>
                        {/* Empty directory */}
                        <SectionHeading
                            title="Looks like you are a new user!"
                            subtitle="Get started by uploading your code library or diving straight in and creating your first house model."
                            className={classes.emptyHeader}
                        />
                        <div className={classes.boxes}>
                            <div className={classes.box}>
                                <CodeLibIcon />
                                <h3>Upload your Code Library</h3>
                                <p>
                                    Long-time HOT2000 user? Upload your code library to bring Volta SNAP up to speed
                                    with all your favourite component assemblies.
                                </p>
                                <Button onClick={() => history.push("/code-library")}>Upload Your Code Library</Button>
                            </div>
                            <div className={classes.box}>
                                <HouseIcon />
                                {/* in this folder */}
                                <h3>Create your first House Model </h3>
                                <p>
                                    Get started on your first model! Have your building's construction PDFs ready to
                                    upload and get ready to start drawing!
                                </p>
                                <Button onClick={() => history.push("/create")} disabled={hasFiveModels}>
                                    Create New Model
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            {!modelDirLoading && (!isEmpty(singleModels) || !isEmpty(sharedFolderEntries) || isInOrgTree) && (
                <div className={classes.dashboardBody}>
                    {error && <p>{error}</p>}
                    <div className={classes.nestedPathsContainer}>
                        {path.length > 2 && <GoBack path={path} history={history} folders={pathFolders} />}
                        <NestedPath
                            path={path}
                            setFolderToDelete={(folderId) => setFolderToDelete(folderId)}
                            setFolderToRename={(folderId) => setFolderToRename(folderId)}
                            folders={pathFolders}
                            setFolderToMove={(folderId) => setFolderToMove(folderId)}
                            setFolderToShare={(folderId) => setFolderToShare(folderId)}
                            onDragOver={onDragUpdate}
                            setModelsToSummarize={() => setModelsToSummarize(filteredModels.map((el) => el[0]))}
                        />
                    </div>
                    <div className={classes.tableHead}>
                        <div
                            className={classes.sortableHead}
                            onClick={() => handleSort("name")}
                            role="button"
                            aria-label={`Sort by name ${sortBy === "name" ? (sortOrder === "asc" ? "ascending" : "descending") : ""}`}
                            {...createKeyboardShortcut({ keys: ["Enter"], onKeyDown: () => handleSort("name") })}
                        >
                            Name
                            {sortBy === "name" && (
                                <span className={classes.sortIndicator} aria-hidden>
                                    {sortOrder === "asc" ? " ↑" : " ↓"}
                                </span>
                            )}
                        </div>
                        <div
                            className={classes.sortableHead}
                            onClick={() => handleSort("address")}
                            role="button"
                            aria-label={`Sort by address ${sortBy === "address" ? (sortOrder === "asc" ? "ascending" : "descending") : ""}`}
                            {...createKeyboardShortcut({ keys: ["Enter"], onKeyDown: () => handleSort("address") })}
                        >
                            Address
                            {sortBy === "address" && (
                                <span className={classes.sortIndicator} aria-hidden>
                                    {sortOrder === "asc" ? " ↑" : " ↓"}
                                </span>
                            )}
                        </div>
                        <div
                            className={classes.sortableHead}
                            onClick={() => handleSort("lastEdited")}
                            role="button"
                            aria-label={`Sort by date modified ${sortBy === "lastEdited" ? (sortOrder === "asc" ? "oldest first" : "newest first") : ""}`}
                            {...createKeyboardShortcut({ keys: ["Enter"], onKeyDown: () => handleSort("lastEdited") })}
                        >
                            Last Modified
                            {sortBy === "lastEdited" && (
                                <span className={classes.sortIndicator} aria-hidden>
                                    {sortOrder === "asc" ? " ↑" : " ↓"}
                                </span>
                            )}
                        </div>
                        {currentFolderId && folders[currentFolderId]?.disableDelete ? (
                            <div></div>
                        ) : (
                            <div className={classes.createNew} ref={createRef}>
                                <span onClick={() => setIsCreateOpen(!isCreateOpen)}>
                                    Create <AddIcon />
                                </span>
                                <Dropdown
                                    className={classes.createDropdown}
                                    open={isCreateOpen}
                                    options={createNewOptions}
                                />
                            </div>
                        )}
                    </div>

                    <ul style={{ userSelect: "none" }} ref={folderModelRef}>
                        {allFoldersModels.map(
                            (
                                [
                                    key,
                                    {
                                        name,
                                        address = "",
                                        complete,
                                        lastEdited,
                                        tags,
                                        sharedWithUser = false,
                                        isInsightsActive = false,
                                        type,
                                        disableDelete = false,
                                        isOrgFolder = false,
                                        ownerUid = "",
                                    },
                                ],
                                index
                            ) => (
                                <div
                                    key={key}
                                    draggable
                                    {...(key === firstModelKey && type === "model"
                                        ? { "data-dashboard-tour": "model-list-item" }
                                        : {})}
                                    onDragStart={(event) => onDragStart(event, key, name, type)}
                                    onDragOver={(event) => {
                                        if (!dragLock) {
                                            event.stopPropagation();
                                            onDragUpdate(event, key, type);
                                        }
                                    }}
                                    onDragEnd={(event) => onDragEnd(event)}
                                    onDragLeave={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();

                                        setDropTo("");
                                    }}
                                    className={classes.draggableModelFolder}
                                    onClick={(event) => onClick(event, type, key, index)}
                                >
                                    {type === "folder" ? (
                                        <>
                                            <Folder
                                                key={key}
                                                folderId={key}
                                                name={name}
                                                sharedWithUser={sharedWithUser}
                                                isOrgFolder={isOrgFolder}
                                                disableDelete={disableDelete}
                                                startDelete={startFolderDelete}
                                                lastEdited={lastEdited}
                                                pathname={pathname === "/" ? "/dashboard" : pathname}
                                                setFolderToRename={() => {
                                                    setFolderToRename(key);
                                                    setNewFolderName(name);
                                                }}
                                                setFolderToMove={(folderId) => setFolderToMove(folderId)}
                                                setFolderToShare={() => setFolderToShare(key)}
                                                tags={tags}
                                                setSelectedFolderModel={setSelectedFolderModel}
                                                selectedFolderModel={selectedFolderModel}
                                                isSelected={selectedModelsFolders.includes(key)}
                                                isDragging={isDragging}
                                                combineWith={
                                                    dropTo === key &&
                                                    !selectedModelsFolders.includes(key) &&
                                                    selectedFolderModel !== key
                                                }
                                                isDropInProcess={
                                                    isDropInProcess &&
                                                    (selectedModelsFolders.includes(key) || key === selectedFolderModel)
                                                }
                                                selectedModelsFolders={selectedModelsFolders}
                                                setFolderModelsToDelete={setFolderModelsToDelete}
                                                disableMultiDelete={dragLock}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <Model
                                                key={key}
                                                modelId={key}
                                                selectModel={selectModel}
                                                name={name}
                                                address={address}
                                                lastEdited={lastEdited}
                                                startDelete={startDelete}
                                                startSharedModelRemove={startSharedModelRemove}
                                                tags={tags}
                                                setModelToMove={(modelId) => setModelToMove(modelId)}
                                                setModelToDuplicate={(modelId) => setModelToDuplicate(modelId)}
                                                setDuplicateName={(name) => setDuplicateName(name)}
                                                sharedWithUser={sharedWithUser}
                                                isOrgModel={isInOrgTree}
                                                ownerName={
                                                    isInOrgTree && ownerUid && ownerUid !== uid
                                                        ? getOrgMemberName(ownerUid)
                                                        : ""
                                                }
                                                removeFromOrgFolder={removeModelFromOrgFolder}
                                                isInsightsActive={isInsightsActive}
                                                setSelectedFolderModel={setSelectedFolderModel}
                                                selectedFolderModel={selectedFolderModel}
                                                isSelected={selectedModelsFolders.includes(key)}
                                                isDragging={isDragging}
                                                isDropInProcess={
                                                    isDropInProcess &&
                                                    (selectedModelsFolders.includes(key) || key === selectedFolderModel)
                                                }
                                                selectedModelsFolders={selectedModelsFolders}
                                                setFolderModelsToDelete={setFolderModelsToDelete}
                                                disableMultiDelete={dragLock}
                                                hasFiveModels={hasFiveModels}
                                                isFirstModel={key === firstModelKey}
                                            />
                                        </>
                                    )}
                                </div>
                            )
                        )}
                    </ul>
                </div>
            )}
            {!modelDirLoading && isEmpty(filteredModels) && isEmpty(filteredFolders) && isInOrgTree && (
                <div className={classes.dashboardEmpty}>
                    <div className={classes.singleBox}>
                        <div></div>
                        <div className={classes.box}>
                            <SharedModel />
                            {orgDirError ? (
                                <>
                                    <h3>This shared folder could not be loaded</h3>
                                    <p>
                                        {orgName} could not be read from this account. Check with an organization admin
                                        that you are still a member.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h3>This shared folder is empty</h3>
                                    <p>
                                        Drag models here from your own directory to share them with everyone in{" "}
                                        {orgName}.
                                    </p>
                                </>
                            )}
                        </div>
                        <div></div>
                    </div>
                </div>
            )}
            {!modelDirLoading &&
                isEmpty(filteredModels) &&
                isEmpty(filteredFolders) &&
                !isInOrgTree &&
                currentFolderId != null && (
                    <div className={classes.dashboardEmpty}>
                        <div className={classes.singleBox}>
                            <div></div>
                            <div className={classes.box}>
                                <HouseIcon />
                                {/* in this folder */}
                                <h3>Create your first House Model in this folder</h3>
                                <Button
                                    disabled={hasFiveModels}
                                    onClick={() =>
                                        history.push(`/create${currentFolderId ? `?folderId=${currentFolderId}` : ""}`)
                                    }
                                >
                                    Create New Model
                                </Button>
                            </div>
                            <div></div>
                        </div>
                    </div>
                )}
            {toDelete && (
                <DeleteDialog
                    singleModels={singleModels}
                    toDelete={toDelete}
                    deleting={deleting}
                    onClick={async () => {
                        toggleDeleting(true);
                        await deleteModel({ modelId: toDelete, uid });
                        toggleDeleting(false);
                        toggleToDelete("");
                    }}
                    toggleToDelete={() => toggleToDelete("")}
                />
            )}
            {toRemoveSharedModel && (
                <DeleteDialog
                    singleModels={singleModels}
                    toDelete={toRemoveSharedModel}
                    deleting={deleting}
                    onClick={async () => {
                        toggleDeleting(true);
                        await removeSharedModelFromDir({ modelId: toRemoveSharedModel, uid });
                        toggleDeleting(false);
                        toggleToRemoveSharedModel("");
                    }}
                    toggleToDelete={() => toggleToRemoveSharedModel("")}
                    isSharedModel={true}
                />
            )}
            {folderToDelete && (
                <DeleteDialog
                    singleModels={folders}
                    toDelete={folderToDelete}
                    deleting={folderLoading}
                    onClick={async () => {
                        const index = path.findIndex((el) => el === folderToDelete);

                        if (index !== -1) {
                            if (index === 0) history.push("/dashboard");

                            if (index > 0) history.push(`/dashboard/${path.slice(0, index).join("/").toString()}`);
                        }

                        await deleteFolder(folderToDelete, uid, mutationOrgId);
                        setFolderToDelete("");
                    }}
                    toggleToDelete={() => setFolderToDelete("")}
                    isFolder
                />
            )}
            {folderModelsToDelete.length > 1 && (
                <DeleteMultipleFilesDialog
                    filesToDelete={folderModelsToDelete}
                    toggleToDelete={() => setFolderModelsToDelete([])}
                    onClick={() => handleMultipleDelete()}
                    deleting={isMultipleDeleting}
                />
            )}
            <DuplicateDialog
                modelToDuplicate={modelToDuplicate}
                newDuplicateName={duplicateName}
                setDuplicateName={(newVal) => setDuplicateName(newVal)}
                onClick={async () => {
                    setDuplicateLoading(true);
                    // a copy belongs to the user, so a copy made inside the shared folder lands in
                    // their own root rather than orphaning itself against an org folder id
                    await duplicateModel(modelToDuplicate, uid, isInOrgTree ? null : currentDirFolderId, duplicateName);
                    if (isInOrgTree) showAlert("Copy saved to your Model Directory");
                    setModelToDuplicate("");
                    setDuplicateLoading(false);
                }}
                close={() => setModelToDuplicate("")}
                duplicateLoading={duplicateLoading}
            />
            <CreateDialog
                toCreate={toCreate}
                newFolderName={folderName}
                setNewFolderName={(newVal) => setFolderName(newVal)}
                onClick={async () => {
                    await createFolder(uid, folderName, currentDirFolderId, mutationOrgId);
                    setToCreate(false);
                    setFolderName("");
                }}
                close={() => setToCreate(false)}
                folderLoading={folderLoading}
            />
            <OrgRequestDialog
                open={reqOrgOpen}
                organizationName={organizationName}
                setOrganizationName={setOrganizationName}
                onClick={async () => {
                    setOrgRequestLoading(true);
                    await sendOrganizationRequest(uid, organizationName);
                    setReqOrgOpen(false);
                    setOrganizationName("");
                    setOrgRequestLoading(false);
                }}
                close={() => setReqOrgOpen(false)}
                orgRequestLoading={orgRequestLoading}
            />
            {!!folderToShare && (
                <ShareFolderDrawer
                    toShare={folderToShare}
                    close={() => setFolderToShare("")}
                    currentFolder={folders[currentFolderId] || {}}
                />
            )}
            <RenameDialog
                toRename={folderToRename}
                oldFolderName={folders[folderToRename]?.name}
                newFolderName={newFolderName}
                onClick={async () => {
                    await renameFolder(folderToRename, uid, newFolderName, mutationOrgId);
                    setNewFolderName("");
                    setFolderToRename("");
                }}
                setNewFolderName={(val) => setNewFolderName(val)}
                close={() => setFolderToRename("")}
                folderLoading={folderLoading}
            />
            <ExportSummaryDialog
                modelIds={modelsToSummarize}
                close={() => setModelsToSummarize([])}
                exportLoading={exportLoading}
                onClick={handleBatchSummaryExport}
            />
            <SelectFolder
                folderToMove={folderToMove}
                modelToMove={modelToMove}
                close={() => {
                    setFolderToMove("");
                    setModelToMove("");
                }}
                rootDir="Model Directory"
                folders={moveDestinationFolders}
                models={moveDestinationModels}
                moveFolder={async (moveToId) => {
                    await moveSelection(folderToMove, moveToId);

                    setSelectedFolderModel("");
                    if (selectedModelsFolders.length > 1) setSelectedModelsFolders([]);
                }}
                moveModel={async (moveToId) => {
                    await moveSelection(modelToMove, moveToId);

                    setSelectedFolderModel("");
                    if (selectedModelsFolders.length > 1) setSelectedModelsFolders([]);
                }}
                folderLoading={folderLoading}
                modelLoading={modelLoading}
                history={history}
            />
            <DashboardTour
                modelDirLoading={modelDirLoading}
                name={name}
                company={company}
                role={role}
                agreedToTerms={agreedToTerms}
            />
            <LegalUpdateNotice />
            <SnackbarAlert
                top={"90%"}
                autoHideDuration={6000}
                onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
                {...alert}
            />
        </div>
    );
};

export default Dashboard;
